/**
 * Gemini Enterprise Agents Service Layer
 * Transforms API responses from the Gemini Enterprise microservice
 * for consumption by React UI components.
 */

import { geminiEnterpriseAPI as enterpriseAIAPI } from '../api/enterpriseAIClient';
import { complianceAPI } from '../api/complianceClient';
import {
  calculateEvidenceWeightedScore,
  summarizeComplianceFrameworks,
  summarizeFrameworkGroup,
} from './complianceScoring';
import { normalizeKnowledgeGap } from './normalizeKnowledgeGap';
import { normalizeImpact, normalizeThreatSeverity } from '../../utils/signalLevels';

const isUnknownAgent = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return !normalized
    || normalized === 'unknown agent'
    || /^(?:[a-z0-9-]+-)?gemini-enterprise[-_]\d+/i.test(normalized);
};
const withoutUnknownAgents = (items = []) => items.filter((item) => (
  !isUnknownAgent(item?.agent_name || item?.agent_display_name || item)
));

const adjustedActiveAgentCount = (count, original = []) => Math.max(
  0,
  Number(count || 0) - (original.length - withoutUnknownAgents(original).length),
);

export const geminiEnterpriseAgentsService = {
  /**
   * Dashboard — aggregated KPIs, charts, and summary data
   */
  async getDashboardStats(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      if (params.include_trends) timeParams.include_trends = true;
      const data = await enterpriseAIAPI.getDashboard(timeParams);
      const rawAgentBreakdown = data.agent_breakdown || [];
      const {
        knowledge_gaps: legacyKnowledgeGaps,
        ...threatStats
      } = data.threat_stats || {};
      return {
        total_interactions: data.total_interactions || 0,
        unique_users: data.unique_users || 0,
        active_agents: adjustedActiveAgentCount(data.active_agents, rawAgentBreakdown),
        active_sessions: data.active_sessions || 0,
        // Unmeasured rates stay null so pages can show "—"/"N/A", never 0% (audit row C16).
        success_rate: data.success_rate ?? null,
        avg_grounding_coverage: data.avg_grounding_coverage ?? null,
        ungrounded_answers: data.ungrounded_answers || 0,
        total_unique_sources: data.total_unique_sources || 0,
        external_source_pct: data.external_source_pct || 0,
        daily_counts_7d: data.daily_counts_7d || [0, 0, 0, 0, 0, 0, 0],
        severity_distribution: data.severity_distribution || {},
        method_distribution: data.method_distribution || {},
        agent_breakdown: withoutUnknownAgents(rawAgentBreakdown),
        timeline: data.timeline || [],
        threat_stats: threatStats,
        coverage_quality: {
          ...(data.coverage_quality || {}),
          knowledge_gaps: data.coverage_quality?.knowledge_gaps ?? legacyKnowledgeGaps ?? 0,
        },
        grounding_policy: data.grounding_policy || {},
        avg_session_quality: data.avg_session_quality ?? null,
        conversation_flow_distribution: data.conversation_flow_distribution || {},
        session_outcome_distribution: data.session_outcome_distribution || {},
        complexity_distribution: data.complexity_distribution || {},
        previous_period: data.previous_period || null,
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  /**
   * Threat stats — canonical severity distribution for the security summary card
   */
  async getThreatStats(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      const data = await enterpriseAIAPI.getDashboard(timeParams);
      const sd = data.severity_distribution || {};
      const severityDistribution = {
        critical: Number(sd.critical) || 0,
        high: Number(sd.high) || 0,
        medium: Number(sd.medium) || 0,
        low: Number(sd.low) || 0,
      };
      const canonicalTotal = data.threat_stats?.total_threats ?? data.total_flagged;
      return {
        total_flagged: Number(canonicalTotal) || 0,
        severity_distribution: severityDistribution,
        ...severityDistribution,
      };
    } catch (error) {
      console.error('Error fetching threat stats:', error);
      throw error;
    }
  },

  /**
   * Conversations (Interactions) — paginated list with filters
   */
  async getConversations(params = {}) {
    try {
      const data = await enterpriseAIAPI.getInteractions({
        page: params.page || 1,
        page_size: params.page_size || params.limit || 50,
        user_email: params.user_email || undefined,
        application: params.agent_name || params.application || undefined,
        state: params.state || undefined,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });

      const conversations = (data.interactions || []).map((i) => {
        // Use obfuscated summaries as fallback for SaaS-stripped traces
        const query = i.query || i.obfuscated_prompt_summary || '';
        const responseSummary = i.response || i.obfuscated_response_summary || '';
        // Build answer_segments: prefer raw segments, then response text, then obfuscated summary
        const segments = i.answer_segments && i.answer_segments.length > 0
          ? i.answer_segments
          : responseSummary
            ? [{ text: responseSummary, referenceIndices: [] }]
            : [];

        return {
          id: i.interaction_id,
          timestamp: i.timestamp,
          engine_id: i.engine_id || '',
          agent_name: i.agent_name || i.application || 'Unknown Agent',
          user_email: i.user_email || '',
          session_id: i.session_id || '',
          method: i.method || 'Unknown',
          state: i.state || 'UNKNOWN',
          model: i.model || null,
          intent_category: i.intent_category || 'uncategorized',
          intent_confidence: i.intent_confidence || 0,
          threat_flag: i.threat_severity && i.threat_severity !== 'none' ? i.threat_severity : i.threat_flag || null,
          query,
          answer_segments: segments,
          references: i.references || [],
          grounding_coverage: i.grounding_coverage ?? null,
          source_count: i.source_count || (i.references || []).length,
          risk_level: i.risk_level || 'none',
          has_pii: i.has_pii || false,
          scan_status: i.scan_status || 'unknown',
          // Response metrics (Improvement #3)
          response_metrics: i.response_metrics || null,
          // Semantic enrichment (Improvement #2)
          semantic: i.semantic || null,
          // Session intelligence (Improvement #1)
          session_intelligence: i.session_intelligence || null,
          // Encryption metadata for client-side decryption
          data_encrypted: i.data_encrypted,
          data_policy: i.data_policy,
          'saf3ai.encryption.wrapped_key': i['saf3ai.encryption.wrapped_key'],
        };
      });

      return {
        conversations,
        total: data.total || conversations.length,
        page: data.page || params.page || 1,
        page_size: data.page_size || params.page_size || 50,
        total_pages: data.total_pages || Math.ceil((data.total || conversations.length) / (params.page_size || 50)),
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  /**
   * Threats — paginated list with severity filter
   */
  async getThreats(params = {}) {
    try {
      const data = await enterpriseAIAPI.getThreats({
        severity: params.severity && params.severity !== 'all' ? params.severity : undefined,
        threat_type: params.threat_type && params.threat_type !== 'all' ? params.threat_type : undefined,
        user_email: params.user_email || undefined,
        agent_name: params.agent_name || undefined,
        page: params.page || 1,
        page_size: params.page_size || 50,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
        include_trends: params.include_trends || undefined,
      });

      const threats = (data.threats || []).map((t) => ({
        id: t.interaction_id || t.id,
        conversation_id: t.interaction_id || t.conversation_id,
        timestamp: t.timestamp,
        agent_name: t.agent_name || 'Unknown Agent',
        user_email: t.user_email || '',
        severity: normalizeThreatSeverity(t.severity),
        threat_type: t.threat_type || 'unknown',
        auto_flagged: t.auto_flagged || false,
        query: t.query || t.obfuscated_prompt_summary || '',
        obfuscated_prompt_summary: t.obfuscated_prompt_summary || '',
        obfuscated_response_summary: t.obfuscated_response_summary || '',
        intent_category: t.intent_category || '',
        detection_categories: t.detection_categories || [],
        classification_reason: t.classification_reason || t.detail || '',
        state: t.state || 'SUCCEEDED',
        risk_factors: t.risk_factors || [],
        has_pii: t.has_pii || false,
        // Specific PII types detected in the prompt/response (SSN, CREDIT_CARD,
        // EMAIL, AADHAAR, API_KEY, …). Feeds the Data Exposure Sankey so it
        // shows per-type nodes instead of a single "Other Sensitive Data" lump.
        pii_types: Array.isArray(t.pii_types) ? t.pii_types : [],
        // Model Armor deep parsing (Improvement #5)
        model_armor: t.model_armor || null,
        // Encryption metadata for client-side decryption
        data_encrypted: t.data_encrypted,
        data_policy: t.data_policy,
        'saf3ai.encryption.wrapped_key': t['saf3ai.encryption.wrapped_key'],
      }));

      return {
        threats,
        total: data.total ?? null, // the server total, never the page length (D2)
        by_severity: data.severity_counts || data.severity_distribution || {},
        page: data.page || 1,
        total_pages: data.total_pages || 1,
        risk_policy: data.risk_policy || {},
        previous_period: data.previous_period || null,
      };
    } catch (error) {
      console.error('Error fetching threats:', error);
      throw error;
    }
  },

  /**
   * Agents (Registry) — list of all discovered agents
   */
  async getAgents(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      if (params.scope) timeParams.scope = params.scope;
      const data = await enterpriseAIAPI.getApplications(timeParams);
      const agents = withoutUnknownAgents(data.agents || data.applications || []).map((a) => ({
        engine_id: a.engine_id || '',
        agent_name: a.agent_name || 'Unknown Agent',
        total_interactions: a.total_interactions || 0,
        unique_sessions: a.unique_sessions || 0,
        unique_users: a.unique_users || 0,
        success_rate: a.success_rate ?? null,
        top_source_domain: a.top_source_domain || (a.source_domains || [])[0] || '',
        last_active: a.last_active || '',
        methods: a.methods || [],
        model: a.model || null,
        source_domains: a.source_domains || [],
        threats: a.threats || 0,
      }));

      return {
        agents,
        total: data.total || agents.length,
      };
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  },

  /**
   * User-Agent Mapping — lightweight aggregation for topology views
   */
  async getUserAgentMapping(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      return await enterpriseAIAPI.getUserAgentMapping(timeParams);
    } catch (error) {
      console.error('Error fetching user-agent mapping:', error);
      throw error;
    }
  },

  /**
   * Sources — grounding source domains and citations
   */
  async getSources(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      // The filter bar's agent / user selection (comma lists), narrowed server-side.
      if (params.agent_name) timeParams.agent_name = params.agent_name;
      if (params.user_email) timeParams.user_email = params.user_email;
      const data = await enterpriseAIAPI.getSources(timeParams);
      return {
        sources: data.sources || [],
        summary: data.summary || {},
        total: data.summary?.total_sources || (data.sources || []).length,
      };
    } catch (error) {
      console.error('Error fetching sources:', error);
      throw error;
    }
  },

  /**
   * Intent Analysis — category distribution and individual intents
   */
  async getIntentAnalysis(params = {}) {
    try {
      const data = await enterpriseAIAPI.getIntentAnalysis({
        ...params,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });
      return {
        summary: data.summary || { total_analyzed: 0, categories_detected: 0, risky_intents: 0 },
        categories: data.categories || [],
        distribution: data.distribution || {},
        intents: (data.intents || []).map((i) => ({
          conversation_id: i.interaction_id || i.conversation_id,
          // The page's user filter, Identifiers, User and Scanner Detection read these;
          // dropping them emptied the table under a user filter and hid those sections.
          interaction_id: i.interaction_id || i.conversation_id || '',
          user_email: i.user_email || '',
          threat_categories: Array.isArray(i.threat_categories) ? i.threat_categories : [],
          has_policy_violation: Boolean(i.has_policy_violation),
          threat_severity: i.threat_severity || 'none',
          content_status: i.content_status || null,
          category: i.category || 'uncategorized',
          confidence: i.confidence || 0,
          query: i.query || '',
          agent_name: i.agent_name || '',
          timestamp: i.timestamp || '',
          // Prefer scanner severity when present (matches Threats page / GE API coalesce)
          risk_level: (
            i.threat_severity && i.threat_severity !== 'none'
              ? i.threat_severity
              : i.risk_level
          ) || 'none',
        })),
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 20,
      };
    } catch (error) {
      console.error('Error fetching intent analysis:', error);
      throw error;
    }
  },

  /**
   * Intent Categories — static metadata
   */
  async getIntentCategories() {
    try {
      const data = await enterpriseAIAPI.getIntentCategories();
      return data;
    } catch (error) {
      console.error('Error fetching intent categories:', error);
      throw error;
    }
  },

  /**
   * Knowledge Gaps — topics where agents lack grounding
   */
  async getKnowledgeGaps(params = {}) {
    try {
      const data = await enterpriseAIAPI.getKnowledgeGaps({
        ...params,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });
      const gaps = (data.gaps || []).map(normalizeKnowledgeGap);

      return {
        gaps,
        total: data.total || gaps.length,
        clustering: data.clustering || null,
        // Where the payload came from: { source: 'pending' } on a cache miss the
        // background worker has not filled yet.
        cache: data.cache || null,
        stats: data.stats || {
          total_gaps: gaps.length,
          high_priority: gaps.filter((g) => g.priority === 'high').length,
          ungrounded_queries: gaps.reduce((sum, g) => sum + g.query_count, 0),
        },
        gap_type_distribution: data.gap_type_distribution || {},
        gap_trend: data.gap_trend || [],
        agent_ranking: data.agent_ranking || [],
        user_impact: data.user_impact || [],
      };
    } catch (error) {
      console.error('Error fetching knowledge gaps:', error);
      throw error;
    }
  },

  /**
   * Usage & ROI — productivity metrics and trends
   */
  /**
   * Token economics for this module.
   *
   * Returns the shared Tokenomics contract. Cost fields come back null — this
   * surface is not billed per token — and the `capabilities` block tells the
   * shared page which cards to render.
   */
  async getTokenomics(params = {}) {
    const timeParams = {};
    if (params.start_time) timeParams.start_time = params.start_time;
    if (params.end_time) timeParams.end_time = params.end_time;
    if (params.agent_name) timeParams.agent_name = params.agent_name;
    if (params.user_email) timeParams.user_email = params.user_email;
    return enterpriseAIAPI.getTokenomics(timeParams);
  },

  async getUsageROI(params = {}) {
    try {
      const timeParams = {};
      if (params.start_time) timeParams.start_time = params.start_time;
      if (params.end_time) timeParams.end_time = params.end_time;
      if (params.agent_name) timeParams.agent_name = params.agent_name;
      if (params.user_email) timeParams.user_email = params.user_email;
      const [dashData, roiData, configData] = await Promise.all([
        enterpriseAIAPI.getDashboard(timeParams),
        enterpriseAIAPI.getUsageROI({ ...timeParams, include_trends: params.include_trends || undefined }).catch(() => null),
        enterpriseAIAPI.getConfig().catch(() => null),
      ]);

      // Read ROI settings from org config, fall back to defaults
      const configObj = configData?.config || configData || {};
      const roiConfig = configObj.roi || {};
      const displayConfig = configObj.display || {};

      // Use ROI endpoint stats if available, else compute from dashboard
      const roiStats = roiData?.stats || {};
      const minutesPerQuery = roiStats.minutes_per_query
        ?? roiConfig.avg_minutes_per_interaction
        ?? 5;
      // The rate the aggregation actually applied wins over the one the config
      // endpoint advertises. Both read from the same org config, so they agree
      // in the normal case — but when they don't, `productivity_value` and the
      // per-tier `weighted_value`s below are already computed, and labelling
      // them "At $X/hr" with a rate that produced none of them is worse than
      // showing the rate that did. Config is the fallback for the shapes that
      // have no stats to describe.
      const hourlyCostRate = roiStats.hourly_cost_rate
        ?? roiConfig.hourly_cost_rate
        ?? 50;

      const totalInteractions = dashData.total_interactions
        ?? roiStats.total_interactions
        ?? 0;
      const rawAgentBreakdown = roiData?.agent_breakdown || dashData.agent_breakdown || [];
      const activeSessions = roiStats.total_sessions
        ?? dashData.active_sessions
        ?? 0;
      const avgQueriesPerSession = roiStats.avg_queries_per_session
        ?? (activeSessions > 0 ? +(totalInteractions / activeSessions).toFixed(2) : 0);
      const estimatedHoursSaved = roiStats.estimated_hours_saved
        ?? +((totalInteractions * minutesPerQuery) / 60).toFixed(2);
      const hasFiniteStat = (value) => value !== null
        && value !== undefined
        && Number.isFinite(Number(value));
      // The ROI endpoint already applies the configured hourly rate and the
      // per-tier complexity weights. Reuse those authoritative totals instead
      // of multiplying the average weight a second time in the UI.
      const productivityValue = hasFiniteStat(roiStats.productivity_value)
        ? Number(roiStats.productivity_value)
        : Math.round(estimatedHoursSaved * hourlyCostRate);
      const complexityAdjustedValue = hasFiniteStat(roiStats.complexity_adjusted_value)
        ? Number(roiStats.complexity_adjusted_value)
        : productivityValue;

      return {
        stats: {
          total_interactions: totalInteractions,
          active_agents: adjustedActiveAgentCount(
            roiStats.active_agents || dashData.active_agents,
            rawAgentBreakdown,
          ),
          unique_users: roiStats.unique_users || dashData.unique_users || 0,
          total_sessions: activeSessions,
          avg_queries_per_session: avgQueriesPerSession,
          estimated_hours_saved: estimatedHoursSaved,
          productivity_value: productivityValue,
          complexity_adjusted_value: complexityAdjustedValue,
          avg_complexity_weight: roiStats.avg_complexity_weight || 1,
          minutes_per_query: minutesPerQuery,
          hourly_cost_rate: hourlyCostRate,
          success_rate: roiStats.success_rate ?? dashData.success_rate ?? null,
          // The usage/ROI endpoint now emits grounding on the same population
          // as the rest of the page; prefer it over the dashboard snapshot.
          avg_grounding_coverage: roiStats.avg_grounding_coverage ?? dashData.avg_grounding_coverage ?? null,
        },
        display: {
          show_estimated_values: displayConfig.show_estimated_values !== false,
          show_grounding_bars: displayConfig.show_grounding_bars !== false,
        },
        monthly_trend: roiData?.monthly_trend || dashData.timeline || [],
        agent_breakdown: withoutUnknownAgents(rawAgentBreakdown).map((a) => ({
          agent_name: a.agent_name,
          interactions: a.interactions || 0,
          sessions: a.sessions || 0,
          success_rate: a.success_rate ?? null,
          pct_of_total: a.pct_of_total || 0,
        })),
        method_distribution: roiData?.method_distribution || dashData.method_distribution || {},
        feedback: roiData?.feedback || {
          satisfaction_rate: 0, thumbs_up: 0, thumbs_down: 0,
          total_feedback: 0, by_agent: [], trend: [],
        },
        complexity_distribution: roiData?.complexity_distribution || [],
        adoption: roiData?.adoption || {
          unique_users: 0, daily_active_users: [], hourly_distribution: [],
        },
        previous_period: roiData?.previous_period || null,
      };
    } catch (error) {
      console.error('Error fetching usage/ROI:', error);
      throw error;
    }
  },

  /**
   * Compliance — overall score, frameworks, and violations
   * Pulls data from the compliance microservice (port 8084)
   */
  async getCompliance(params = {}) {
    return this.getModuleCompliance('gemini-enterprise', params);
  },

  /**
   * The Gemini Enterprise compliance build for any module on the compliance
   * MS: Endpoint Security renders this same page with module 'shadow-ai'.
   * Everything module-specific below follows `module`.
   */
  async getModuleCompliance(module, params = {}) {
    try {
      // Compliance MS endpoints use start_date/end_date, not start_time/end_time
      const sourceFilter = {
        // Do not hardcode source index; deployments can persist enterprise traces
        // into saf3ai-otel-traces while still using gemini-enterprise module.
        // Let backend module + org/date filters resolve the right index.
        module,
        start_date: params.start_time || undefined,
        end_date: params.end_time || undefined,
      };
      // Pass through time_range if provided (e.g., 'last_24_hours', 'last_7_days')
      if (params.time_range) sourceFilter.time_range = params.time_range;
      if (params.agent_id) sourceFilter.agent_id = params.agent_id;
      if (params.user_email) sourceFilter.user_email = params.user_email;
      const [summaryData, controlSummaryData, frameworksData, configData] = await Promise.all([
        complianceAPI.getComplianceSummary(sourceFilter),
        complianceAPI.getControlSummary(sourceFilter),
        complianceAPI.getAllFrameworks({ module, include_custom: true }),
        // the enabled frameworks are Gemini Enterprise's own setting: another module mounting this
        // build (Endpoint has no framework settings) shows every framework, as its score counts
        module === 'gemini-enterprise' ? enterpriseAIAPI.getConfig().catch(() => null) : null,
      ]);

      const summaryFrameworks = summaryData.frameworks || [];
      const controlSummaries = controlSummaryData.controls || [];
      let allFrameworks = frameworksData || [];
      
      console.log('[Compliance API] Raw frameworksData:', {
        total: allFrameworks.length,
      });
      console.log('[Compliance API] Summary frameworks:', {
        total: summaryFrameworks.length,
        custom_in_summary: summaryFrameworks.filter(fw => fw.is_custom).length
      });

      // Merge custom frameworks from summary that aren't in allFrameworks
      // (custom frameworks might only exist in summary data after first scan)
      // CRITICAL: Module isolation - only merge frameworks that belong to gemini-enterprise
      const frameworkIds = new Set(allFrameworks.map(fw => String(fw.framework_id)));
      
      // Get the set of framework IDs that backend explicitly returned as custom for this module
      const backendCustomFrameworkIds = new Set(
        allFrameworks
          .filter(fw => fw.is_custom === true)
          .map(fw => String(fw.framework_id))
      );
      
      console.log('[Compliance] Backend returned custom frameworks:', Array.from(backendCustomFrameworkIds));
      console.log('[Compliance] Summary has custom frameworks:', 
        summaryFrameworks.filter(fw => fw.is_custom).map(fw => `${fw.framework_name} (${fw.framework_id}, module: ${fw.module || 'none'})`));
      
      for (const summaryFw of summaryFrameworks) {
        if (!summaryFw.is_custom) continue;
        
        const summaryFwId = String(summaryFw.framework_id);
        
        // Skip if already in allFrameworks
        if (frameworkIds.has(summaryFwId)) {
          continue;
        }
        
        // CRITICAL SECURITY CHECK: Verify this framework actually belongs to gemini-enterprise
        // Strategy: If module field exists, it MUST match. If missing, verify via backend query.
        const summaryModule = summaryFw.module;
        
        if (summaryModule) {
          // Module field exists - enforce strict matching
          if (summaryModule !== module) {
            console.log('[Compliance] BLOCKED: Cross-module framework from summary:', 
              summaryFw.framework_name, 'has module:', summaryModule, 'expected:', module);
            continue;
          }
        } else {
          // Module field missing (legacy data) - this is suspicious
          // Since backend should have returned ALL gemini-enterprise frameworks,
          // a missing framework with no module field likely belongs to another module
          console.log('[Compliance] BLOCKED: Framework from summary has no module field:', 
            summaryFw.framework_name, '- likely cross-module contamination');
          continue;
        }
        
        console.log('[Compliance] Adding valid custom framework from summary:', summaryFw.framework_name);
        
        // Build controls from control summary
        const fwControls = controlSummaries
          .filter(ctrl => String(ctrl.framework_id) === summaryFwId)
          .map(ctrl => ({
            control_id: ctrl.control_id,
            control_number: ctrl.control_number,
            name: ctrl.control_name,
            description: '',
            rules: []
          }));
        
        allFrameworks.push({
          framework_id: summaryFw.framework_id, 
          name: summaryFw.framework_name,
          description: '',
          icon: 'shield',
          is_custom: true,
          module, // Explicitly set module
          controls: fwControls
        });
      }

      // Filter by enabled frameworks from org config settings
      // Custom frameworks are always shown regardless of config
      const enabledIds = configData?.config?.compliance?.frameworks;
      if (enabledIds && enabledIds.length > 0) {
        const enabledIdSet = new Set(enabledIds.map(String));
        console.log('[Compliance] Before filter - allFrameworks count:', allFrameworks.length, 
          'custom:', allFrameworks.filter(fw => fw.is_custom).length);
        allFrameworks = allFrameworks.filter(fw => 
          fw.is_custom || enabledIdSet.has(String(fw.framework_id))
        );
        console.log('[Compliance] After filter - allFrameworks count:', allFrameworks.length,
          'custom:', allFrameworks.filter(fw => fw.is_custom).length);
      }

      // Build control summary lookup: control_id -> { violations, total_scanned, compliance_percentage }
      // Index under multiple key shapes so built-in numeric IDs and string custom IDs
      // always resolve the same row as the OpenSearch aggregation / frameworks payload.
      const controlStatsMap = {};
      for (const cs of controlSummaries) {
        const id = cs.control_id;
        controlStatsMap[id] = cs;
        controlStatsMap[String(id)] = cs;
        if (typeof id === 'string' && /^\d+$/.test(id)) {
          controlStatsMap[Number(id)] = cs;
        } else if (typeof id === 'number') {
          controlStatsMap[String(id)] = cs;
        }
      }

      const controlStatsFor = (controlId) =>
        controlStatsMap[controlId]
        ?? controlStatsMap[String(controlId)]
        ?? {};

      // Build framework summary lookup: framework_id -> summary
      const frameworkStatsMap = {};
      for (const fs of summaryFrameworks) {
        const fid = fs.framework_id;
        frameworkStatsMap[fid] = fs;
        frameworkStatsMap[String(fid)] = fs;
        if (typeof fid === 'string' && /^\d+$/.test(fid)) {
          frameworkStatsMap[Number(fid)] = fs;
        } else if (typeof fid === 'number') {
          frameworkStatsMap[String(fid)] = fs;
        }
      }

      const frameworkStatsFor = (frameworkId) =>
        frameworkStatsMap[frameworkId]
        ?? frameworkStatsMap[String(frameworkId)]
        ?? {};

      // The interaction denominator is shared by every framework. Violation and
      // passing-control totals are reconciled from the same control rows rendered
      // by the UI below, so the headline, framework cards, and drawer cannot drift.
      const totalScanned = summaryData.total_scanned ?? 0;

      // Transform frameworks into UI shape
      const frameworks = allFrameworks.map((fw) => {
        const fwStats = frameworkStatsFor(fw.framework_id);
        const fwControls = (fw.controls || []).map((ctrl) => {
          const ctrlStats = controlStatsFor(ctrl.control_id);
          const hasSummary = Object.keys(ctrlStats || {}).length > 0;
          const violations = ctrlStats.violations ?? 0;
          const scanned = ctrlStats.total_scanned ?? 0;
          // Do not default missing summary rows to 100% (this caused false all-green
          // dashboards right after backfill clears impacts before aggregates catch up).
          // CMP-155: a present summary row can itself carry compliance_percentage: null
          // (no detection mapping, or a disabled/empty custom ruleset) — keep that
          // honest, never a fabricated 0%. A genuinely missing summary row keeps the
          // pre-existing "fail" default.
          const notTestable = hasSummary && ctrlStats.compliance_percentage == null;
          const score = notTestable ? null : (hasSummary ? ctrlStats.compliance_percentage : 0);
          const status = notTestable
            ? 'not-evaluated'
            : (!hasSummary ? 'fail' : (violations > 0 ? (score >= 70 ? 'warning' : 'fail') : 'pass'));

          return {
            id: ctrl.control_number || `CTRL-${ctrl.control_id}`,
            control_id: ctrl.control_id,
            name: ctrl.name,
            description: ctrl.description || '',
            score: score == null ? null : Math.round(score),
            status,
            violations,
            total_scanned: scanned,
            rules: ctrl.rules || [],  // preserve rules so UI can show diagnostics
            audit_logs: [], // loaded lazily via getComplianceLogs
          };
        });

        const fwTotalControls = fwControls.length;
        const fwPassingControls = fwControls.filter((c) => c.status === 'pass').length;
        const sumCtrlViolations = fwControls.reduce((s, c) => s + (c.violations ?? 0), 0);
        const fwViolations = sumCtrlViolations;
        const reportedFwScore =
          fwStats.compliance_percentage != null
            ? fwStats.compliance_percentage
            : (fwTotalControls > 0 ? Math.round((fwPassingControls / fwTotalControls) * 100) : 100);
        // Same definition as the headline Compliance Score (controls passing /
        // controls evaluated). The evidence-weighted check ratio rendered
        // "EU AI Act 0/7 passing" as "98 Compliant" under a 27% headline.
        const fwScore = summarizeFrameworkGroup([{
          passingControls: fwPassingControls,
          totalControls: fwTotalControls,
        }]).overall_score ?? Math.round(reportedFwScore);
        // Secondary metric: share of evaluated control checks without a
        // violation. Shown under its own label, never as the card's score.
        const checkPassRate = calculateEvidenceWeightedScore([{
          totalControls: fwTotalControls,
          score: reportedFwScore,
          controls: fwControls,
        }]);
        const fwStatus = fwScore >= 90 ? 'compliant' : fwScore >= 70 ? 'needs-attention' : 'non-compliant';

        return {
          id: fw.framework_id,
          name: fw.name,
          fullName: fw.name,
          description: fw.description || '',
          icon: fw.icon || 'shield',
          score: Math.round(fwScore),
          checkPassRate,
          status: fwStatus,
          passingControls: fwPassingControls,
          totalControls: fwTotalControls,
          violations: fwViolations,
          total_scanned: fwStats.total_scanned ?? totalScanned,
          controls: fwControls,
          isCustom: fw.is_custom || false,
        };
      });

      // Keep certified standards and user-authored rules as separate scorecards.
      // Only the certified pooled controls-met result drives the headline.
      const { certified: certifiedStats, custom: customStats } = summarizeComplianceFrameworks(frameworks);
      const overallScore = certifiedStats.overall_score;
      const overallStatus = certifiedStats.overall_status;
      const totalViolations = certifiedStats.total_violations + customStats.total_violations;

      return {
        stats: {
          overall_score: overallScore,
          overall_status: overallStatus,
          passing_controls: certifiedStats.passing_controls,
          total_controls: certifiedStats.total_controls,
          total_violations: totalViolations,
          scan_coverage: totalScanned > 0 ? 100 : 0,
          total_scanned: totalScanned,
          certified: {
            ...certifiedStats,
            total_scanned: totalScanned,
          },
          custom: {
            ...customStats,
            total_scanned: totalScanned,
          },
          score_basis: 'certified-controls-met',
        },
        frameworks,
        violations: [], // violations are shown per-control via audit logs
      };
    } catch (error) {
      console.error('Error fetching compliance:', error);
      throw error;
    }
  },

  /**
   * Load audit logs for a specific compliance control (lazy loading)
   */
  async getComplianceAuditLogs(controlId, params = {}) {
    try {
      const data = await complianceAPI.getComplianceLogs({
        control_id: controlId,
        per_page: 50,
        source: 'saf3ai-gemini-enterprise-traces',
        ...params,
      });
      return (data.logs || []).map((log) => ({
        status: log.is_violation ? 'violation' : 'pass',
        severity: log.severity || 'medium',
        // Prefer display name; fall back to raw agent_id string for the Agent Name row
        app: log.agent_display_name || String(log.agent_id || ''),
        agent_id: log.agent_id != null ? String(log.agent_id) : null,
        agent_name: log.agent_display_name || String(log.agent_id || ''),
        framework_name: log.framework_name || '',
        control_name: log.control_name || '',
        user: log.user_email || '',
        // span_id (log_id) serves as the trace identifier
        trace_id: log.log_id || '',
        timestamp: log.span_timestamp || log.created_at || '',
        prompt_preview: (log.detection_keys || []).join(', ') || 'Detection triggered',
        entities: log.detection_keys || [],
        detail: `Source: ${log.source_index || 'unknown'}`,
        log_id: log.log_id,
        source_index: log.source_index || log.index || log._index || 'saf3ai-gemini-enterprise-traces',
      }));
    } catch (error) {
      console.error('Error fetching compliance audit logs:', error);
      return [];
    }
  },

  /**
   * Audit Logs — paginated event log
   */
  async getAuditLogs(params = {}) {
    try {
      const data = await enterpriseAIAPI.getAuditLogs({
        event_type: params.event_type || undefined,
        user_email: params.user_email || undefined,
        agent_name: params.agent_name || undefined,
        page: params.page || 1,
        page_size: params.page_size || 50,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });

      return {
        logs: data.logs || data.events || [],
        total: data.total || 0,
        page: data.page || 1,
        page_size: data.page_size || 50,
        total_pages: data.total_pages || 1,
        stats: data.stats || {},
        event_type_distribution: data.event_type_distribution || {},
      };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  /**
   * Audit Log Filters — available filter options
   */
  async getAuditLogFilters() {
    try {
      return await enterpriseAIAPI.getAuditLogFilters();
    } catch (error) {
      console.error('Error fetching audit log filters:', error);
      throw error;
    }
  },

  /**
   * Audit Log Export — every record in the time window (GET /audit-log/export).
   * The endpoint takes only start_time / end_time (no event type / agent / user
   * filter) and returns at most 10,000 rows with the list columns only
   * (timestamp, event type, agent, user, method, state, interaction id).
   */
  async exportAuditLogs(params = {}) {
    try {
      const data = await enterpriseAIAPI.exportAuditLogs({
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });
      const logs = data?.logs || data?.events || [];
      return { logs, total: data?.total ?? logs.length };
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  },

  /**
   * Display Settings — org-specific UI preferences (date range, page size, visibility flags)
   */
  async getDisplaySettings(organizationId) {
    try {
      const config = await this.getConfig(organizationId);
      const display = config?.display || {};
      return {
        default_date_range: display.default_date_range || '7d',
        items_per_page: display.items_per_page || 50,
        show_estimated_values: display.show_estimated_values !== false,
        show_grounding_bars: display.show_grounding_bars !== false,
      };
    } catch (error) {
      console.error('Error fetching display settings:', error);
      return {
        default_date_range: '7d',
        items_per_page: 50,
        show_estimated_values: true,
        show_grounding_bars: true,
      };
    }
  },

  /**
   * Configuration — org settings
   */
  async getConfig(organizationId) {
    try {
      const data = await enterpriseAIAPI.getConfig(organizationId);
      return data.config || data;
    } catch (error) {
      console.error('Error fetching config:', error);
      throw error;
    }
  },

  async updateConfig(organizationId, configData) {
    try {
      const data = await enterpriseAIAPI.updateConfig(organizationId, configData);
      return data.config || data;
    } catch (error) {
      console.error('Error updating config:', error);
      throw error;
    }
  },

  async resetConfig(organizationId) {
    try {
      const data = await enterpriseAIAPI.resetConfig(organizationId);
      return data.config || data;
    } catch (error) {
      console.error('Error resetting config:', error);
      throw error;
    }
  },

  /**
   * Users — paginated user list with activity stats
   */
  async getUsers(params = {}) {
    try {
      const data = await enterpriseAIAPI.getUsers({
        page: params.page || 1,
        page_size: params.page_size || 50,
        risk_level: params.risk_level,
        search: params.search,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
        agent_name: params.agent_name || undefined,
        user_email: params.user_email || undefined,
      });

      const users = (data.users || []).map(u => ({
        user_id: u.user_email,
        user_email: u.user_email,
        interactions: u.interactions || 0,
        total_interactions: u.interactions || 0,
        sessions: u.sessions || 0,
        agents_used: u.agents_used || 0,
        success_rate: u.success_rate ?? null,
        threats: u.threats || 0,
        critical_threats: u.critical_threats || 0,
        high_threats: u.high_threats || 0,
        medium_threats: u.medium_threats || 0,
        low_threats: u.low_threats || 0,
        pii_detected: u.pii_detected || 0,
        high_risk_queries: u.high_risk_queries || 0,
        avg_risk_score: u.avg_risk_score || 0,
        applications_used: u.agents || [],
        last_activity: u.last_activity,
        risk_level: u.risk_level || 'none',
        // Behavioral baseline (Improvement #7)
        baseline_deviation: u.baseline_deviation || null,
        behavioral_flags: u.behavioral_flags || [],
      }));

      return {
        users,
        total: data.total || users.length,
        by_risk: data.risk_counts || {
          high: users.filter(u => u.risk_level === 'high').length,
          medium: users.filter(u => u.risk_level === 'medium').length,
          low: users.filter(u => u.risk_level === 'low').length,
          none: users.filter(u => u.risk_level === 'none').length,
        },
        threat_severity_totals: data.threat_severity_totals || { critical: 0, high: 0, medium: 0, low: 0 },
        total_users: data.total_users || users.length,
        safe_users: data.safe_users || users.filter(u => u.risk_level === 'none').length,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Sessions — session list
   */
  async getSessions(params = {}) {
    try {
      return await enterpriseAIAPI.getSessions(params);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      throw error;
    }
  },

  /**
   * Session Filters — available filter values for the sessions page
   */
  async getSessionFilters(params = {}) {
    try {
      const data = await enterpriseAIAPI.getSessionFilters(params);
      if ((data?.agents || []).length > 0 || (data?.users || []).length > 0) {
        return { ...data, agents: withoutUnknownAgents(data.agents || []) };
      }
      // Empty lists: fall through to the compliance-derived fallback below.
    } catch (error) {
      console.debug('Error fetching session filters:', error);
    }
    // The Gemini Enterprise MS can be unavailable (e.g. local dev runs only the
    // compliance MS) or not yet populated. Compliance impacts know the same
    // agent display names and user emails — both filter correctly against the
    // compliance endpoints — so filters stay usable instead of "None available".
    try {
      const sourceFilter = {
        module: 'gemini-enterprise',
        start_date: params.start_time || undefined,
        end_date: params.end_time || undefined,
      };
      const [agentsRes, usersRes] = await Promise.all([
        complianceAPI.getAgents(sourceFilter),
        complianceAPI.getUsers(sourceFilter),
      ]);
      const agents = withoutUnknownAgents([...new Set(
        (agentsRes?.agents || []).map((a) => a.agent_display_name || a.agent_name).filter(Boolean)
      )]);
      const users = [...new Set(
        (usersRes?.users || []).map((u) => u.user_email).filter(Boolean)
      )];
      return { agents, users, threat_levels: [], methods: [] };
    } catch (fallbackError) {
      console.debug('Error fetching session filters (compliance fallback):', fallbackError);
      return { agents: [], users: [], threat_levels: [], methods: [] };
    }
  },

  /**
   * Session Details — all interactions within a specific session
   */
  async getSessionDetails(sessionId) {
    try {
      return await enterpriseAIAPI.getSessionDetails(sessionId);
    } catch (error) {
      console.error('Error fetching session details:', error);
      throw error;
    }
  },

  /**
   * Health check
   */
  async getHealth() {
    try {
      return await enterpriseAIAPI.getHealth();
    } catch (error) {
      console.error('Error fetching data store health:', error);
      throw error;
    }
  },

  /**
   * Grounding Analytics — percentile distributions, agent performance, domain breakdown
   */
  async getGroundingAnalytics(params = {}) {
    try {
      return await enterpriseAIAPI.getGroundingAnalytics({
        ...params,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
        // The filter bar's agent / user selection (comma lists), narrowed server-side.
        agent_name: params.agent_name || undefined,
        user_email: params.user_email || undefined,
      });
    } catch (error) {
      console.error('Error fetching grounding analytics:', error);
      throw error;
    }
  },

  /**
   * Posture Analysis — IAM analysis + configuration drift metrics
   */
  async getPostureMetrics() {
    try {
      return await enterpriseAIAPI.getPostureMetrics();
    } catch (error) {
      console.error('Error fetching posture metrics:', error);
      throw error;
    }
  },

  /**
   * Posture Analysis — manually trigger a posture scan
   */
  async triggerPostureScan() {
    try {
      return await enterpriseAIAPI.triggerPostureScan();
    } catch (error) {
      console.error('Error triggering posture scan:', error);
      throw error;
    }
  },

  /**
   * Feedback Summary — thumbs up/down analytics
   */
  async getFeedbackSummary(params = {}) {
    try {
      return await enterpriseAIAPI.getFeedbackSummary(params);
    } catch (error) {
      console.error('Error fetching feedback summary:', error);
      throw error;
    }
  },

  /**
   * Posture Findings — query posture findings from backend /posture/findings endpoint
   */
  async getPostureFindings(params = {}) {
    try {
      return await enterpriseAIAPI.getPostureFindings({
        page: params.page || 1,
        page_size: params.page_size || 100,
        ...params,
      });
    } catch (error) {
      // Rethrown: `{ findings: [], total: 0 }` read as "No posture findings yet"
      // and hid the page's `partial` flag (audit row C17).
      console.error('Error fetching posture findings:', error);
      throw error;
    }
  },

  /**
   * Data Access Graph — user→agent→source access paths with shadow access detection
   */
  async getDataAccessGraph(params = {}) {
    try {
      return await enterpriseAIAPI.getDataAccessGraph(params);
    } catch (error) {
      console.error('Error fetching data access graph:', error);
      return { access_paths: [], shadow_access_findings: [], summary: {} };
    }
  },

  /**
   * Source Sensitivity — admin-managed sensitivity overrides
   */
  async getSourceSensitivity() {
    try {
      return await enterpriseAIAPI.getSourceSensitivity();
    } catch (error) {
      console.error('Error fetching source sensitivity:', error);
      return { overrides: [] };
    }
  },

  async updateSourceSensitivity(sourceId, level, notes = '') {
    try {
      return await enterpriseAIAPI.updateSourceSensitivity({
        source_id: sourceId,
        sensitivity_level: level,
        notes,
      });
    } catch (error) {
      console.error('Error updating source sensitivity:', error);
      throw error;
    }
  },

  /**
   * Recommendations — contextual, rule-based recommendations
   */
  async getRecommendations(params = {}) {
    try {
      const data = await enterpriseAIAPI.getRecommendations({
        page: params.page || undefined,
        start_time: params.start_time || undefined,
        end_time: params.end_time || undefined,
      });
      return {
        recommendations: (data.recommendations || []).map((r) => ({
          rule_id: r.rule_id || null,
          icon: r.icon || 'zap',
          title: r.title || '',
          description: r.description || '',
          impact: normalizeImpact(r.impact),
          tag: r.tag || null,
          page: r.page || [],
          entity_id: r.entity_id || null,
          link: r.link || null,
        })),
        total: data.total || 0,
      };
    } catch (error) {
      console.debug('Recommendations not available:', error.message);
      return { recommendations: [], total: 0 };
    }
  },

  /**
   * Adoption Analytics — license utilization, funnel, segmentation.
   *
   * A block the endpoint does not send is null, and a failed request throws: the caller shows
   * its error state. Zeros are never made up for licences, the funnel or the segments (a zero
   * funnel read as "nobody uses it", which is not what an unreachable endpoint means).
   */
  async getAdoptionMetrics(params = {}) {
    const data = await enterpriseAIAPI.getAdoptionMetrics({
      // Adoption owns its windows: First Query is all-time and the
      // windowed cohorts use a rolling seven-day period. Do not forward the
      // page's date filter into this panel request.
      agent_name: params.agent_name || undefined,
      user_email: params.user_email || undefined,
    });
    return {
      license: data?.license ?? null,
      funnel: data?.funnel ?? null,
      segments: data?.segments ?? null,
      segmented_users: data?.segmented_users || [],
      champions: data?.champions || [],
      at_risk: data?.at_risk || [],
      agent_quality: data?.agent_quality || [],
    };
  },

  // AI-driven conversation drift analysis. Sends the session's user prompt
  // + AI chunked responses to the AI microservice, which runs an LLM to
  // detect drift / hallucination / risk signals. Cached server-side by
  // session_id so repeated opens are instant. Exposing this enables the
  // shared "Saf3AI Conversation Analysis" card in the session detail panel.
  async getConversationDriftAnalysis({ session_id, user_prompt, ai_responses, application, user_email, content_status, force_regenerate = false }) {
    try {
      return await enterpriseAIAPI.analyzeConversationDrift({
        session_id,
        user_prompt,
        ai_responses,
        application,
        user_email,
        content_status,
      }, { force_regenerate });
    } catch (err) {
      console.error('Error fetching drift analysis:', err);
      return { ai_summary: null, error: err?.message || 'Failed to load drift analysis' };
    }
  },
};

export default geminiEnterpriseAgentsService;
